/**
 * Background service to sync pending photos when device comes online
 */

import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { db, storage } from '@/firebase/db'
import { offlinePhotoStorage } from './offlineStorage'

interface SyncProgress {
  entityId: string
  entityType: string
  status: 'uploading' | 'updating' | 'complete' | 'error'
  progress: number
  error?: string
}

type SyncCallback = (progress: SyncProgress) => void

class PhotoSyncService {
  private isSyncing = false
  private syncCallbacks: SyncCallback[] = []
  private onlineListener: (() => void) | null = null

  /**
   * Initialize the sync service
   * Sets up online/offline event listeners
   */
  init() {
    // Listen for online event
    this.onlineListener = () => {
      console.log('Device is online, checking for pending photos...')
      this.syncPendingPhotos()
    }

    // Only show offline alert when we actually detect an offline event
    // Don't rely on initial navigator.onLine state as it's unreliable
    window.addEventListener('online', this.onlineListener)

    // Wait a bit for Firebase to initialize then try to sync
    // This will be triggered by the online event if needed
    setTimeout(() => this.syncPendingPhotos(), 2000)
  }

  /**
   * Cleanup event listeners
   */
  destroy() {
    if (this.onlineListener) {
      window.removeEventListener('online', this.onlineListener)
      this.onlineListener = null
    }
  }

  /**
   * Subscribe to sync progress updates
   */
  onSyncProgress(callback: SyncCallback) {
    this.syncCallbacks.push(callback)
    return () => {
      this.syncCallbacks = this.syncCallbacks.filter((cb) => cb !== callback)
    }
  }

  private notifyProgress(progress: SyncProgress) {
    for (const cb of this.syncCallbacks) {
      cb(progress)
    }
  }

  /**
   * Manually trigger sync (useful for retry)
   */
  async syncPendingPhotos(): Promise<void> {
    if (this.isSyncing) {
      console.log('Sync already in progress, skipping...')
      return
    }

    // Don't check navigator.onLine here - it's unreliable
    // This method will be called when online event fires or manually via retry
    this.isSyncing = true

    try {
      // Get all pending photos from IndexedDB
      const pendingPhotos = await offlinePhotoStorage.getAllPendingPhotos()

      if (pendingPhotos.length === 0) {
        console.log('No pending photos to sync')
        return
      }

      console.log(`Found ${pendingPhotos.length} entities with pending photos`)

      // Process each entity
      for (const photoData of pendingPhotos) {
        try {
          await this.syncEntityPhotos(photoData)
        } catch (error) {
          console.error(`Failed to sync photos for ${photoData.id}:`, error)
          this.notifyProgress({
            entityId: photoData.id,
            entityType: photoData.entityType,
            status: 'error',
            progress: 0,
            error: error instanceof Error ? error.message : 'Unknown error',
          })
        }
      }
    } finally {
      this.isSyncing = false
    }
  }

  /**
   * Sync photos for a specific entity from IndexedDB
   */
  private async syncEntityPhotos(
    photoData: Awaited<ReturnType<typeof offlinePhotoStorage.getPhotos>>,
  ): Promise<void> {
    if (!photoData) return

    const { id, entityType, files, metadata } = photoData
    photoData

    console.log(`Syncing ${files.length} photos for ${entityType}/${id}`)

    this.notifyProgress({
      entityId: id,
      entityType,
      status: 'uploading',
      progress: 0,
    })

    // Build the full document path using metadata
    const docRef = doc(
      db,
      'companies',
      metadata.companyId,
      'sites',
      metadata.siteId,
      'units',
      metadata.unitId,
      entityType,
      id,
    )

    // Verify document exists BEFORE uploading photos to Storage
    // This prevents orphaned photos when there's a race condition between
    // document creation and photo sync (especially when online)
    const docSnap = await getDoc(docRef)
    if (!docSnap.exists()) {
      console.log(
        `Document ${entityType}/${id} not found yet, skipping photo sync (will retry on next sync)`,
      )
      // Leave photos in IndexedDB for next sync attempt - don't notify error
      // since this is expected during race conditions
      return
    }

    // Upload photos to Storage
    const uploadedUrls: string[] = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const timestamp = Date.now()
      const randomId = Math.random().toString(36).substring(2, 15)
      const fileName = `${timestamp}-${randomId}-${i}.${file.name.split('.').pop() || 'jpg'}`

      // Determine storage path based on entity type
      let storagePath: string

      if ('subEntityType' in photoData) {
        // For nested entities (repairs/replacements/alterations)
        storagePath = `companies/${metadata.companyId}/sites/${metadata.siteId}/units/${metadata.unitId}/${photoData.subEntityType}s/${id}/${fileName}`
      } else {
        storagePath = `companies/${metadata.companyId}/sites/${metadata.siteId}/units/${metadata.unitId}/${entityType}/${id}/${fileName}`
      }

      try {
        const fileRef = ref(storage, storagePath)

        await uploadBytes(fileRef, file)
        const downloadUrl = await getDownloadURL(fileRef)
        uploadedUrls.push(downloadUrl)

        this.notifyProgress({
          entityId: id,
          entityType,
          status: 'uploading',
          progress: ((i + 1) / files.length) * 50, // 0-50% for uploads
        })
      } catch (uploadError) {
        console.error('Failed to upload photo:', uploadError)
        throw uploadError
      }
    }

    this.notifyProgress({
      entityId: id,
      entityType,
      status: 'updating',
      progress: 60,
    })

    // Update Firestore document - replace blob URLs with Storage URLs
    if ('subEntityType' in photoData) {
      // For nested entities (repairs/replacements/alterations within repair documents)
      console.log(
        `Updating nested entity ${photoData.subEntityType}[${photoData.subEntityIndex}] in ${entityType}/${id}`,
      )

      // Re-fetch document to get latest array state (we already verified it exists above)
      const freshDocSnap = await getDoc(docRef)
      const docData = freshDocSnap.data()

      if (!docData) {
        // Document was deleted between our check and now - rare but possible
        console.error(
          `Document ${entityType}/${id} was deleted, skipping (will retry)`,
        )
        return
      }

      // Determine which array to update based on subEntityType
      const arrayField = `${photoData.subEntityType}s` // e.g., 'repair' -> 'repairs'
      const items = docData[arrayField] || []

      if (photoData.subEntityIndex >= items.length) {
        console.error(
          `Invalid index ${photoData.subEntityIndex} for ${arrayField} (length: ${items.length})`,
        )
        // Don't delete from IndexedDB - this might be a data consistency issue
        return
      }

      // Update the photos for the specific item
      items[photoData.subEntityIndex].photos = uploadedUrls

      // Update the document with the modified array
      await updateDoc(docRef, {
        [arrayField]: items,
      })

      console.log(
        `Successfully updated ${arrayField}[${photoData.subEntityIndex}] with ${uploadedUrls.length} photos`,
      )
    } else if (entityType === 'surveys') {
      // For surveys: photos are stored at surveyData.photos (not root level)
      // This matches the import script behavior and the survey data structure
      await updateDoc(docRef, {
        'surveyData.photos': uploadedUrls,
      })
      console.log(
        `Successfully updated survey with ${uploadedUrls.length} photos at surveyData.photos`,
      )
    } else {
      // For other top-level entities (callbacks, repairs, etc.)
      // Replace blob URLs with real Storage URLs
      await updateDoc(docRef, {
        photos: uploadedUrls,
      })
    }

    // Only clean up IndexedDB after successful Firestore update
    // (if we reach here, the update succeeded - errors throw and are caught by caller)
    this.notifyProgress({
      entityId: id,
      entityType,
      status: 'complete',
      progress: 100,
    })

    await offlinePhotoStorage.deletePhotos(id)

    console.log(`Successfully synced photos for ${entityType}/${id}`)
  }

  /**
   * Check if there are pending uploads
   */
  async hasPendingUploads(): Promise<boolean> {
    const count = await offlinePhotoStorage.getCount()
    return count > 0
  }

  /**
   * Get count of pending uploads
   */
  async getPendingCount(): Promise<number> {
    return offlinePhotoStorage.getCount()
  }
}

export const photoSyncService = new PhotoSyncService()
